import { useMemo } from "react";
import { useUnitsAndCurrency } from "@/hooks/useUnitsAndCurrency";
import { useUnits } from "@/query/hooks";
import { calculateFlowTotal } from "../../services/calculationService";
import { useProductLookups } from "../../contexts/ProductLookupsContext";
import { getAiChangeValues } from "./AiChangeBadge";
import { isProposalFlow } from "../../types/proposal.types";
import type { FlowCellProps } from "./types";
import { cn } from "@/lib/utils";
import { PreOrderCategory } from "@/app/api/types/preOrder";

export function TotalCell({ flow, preOrder }: FlowCellProps) {
  const { currencySymbol } = useUnitsAndCurrency();
  const { data: units = [] } = useUnits();
  const lookups = useProductLookups();

  // Mirror PriceCell's effective-price ladder so the row total never
  // disagrees with the value shown in PriceCell:
  //   1. user-committed `flow.price_per_weight` (non-null AND non-zero).
  //      Legacy DB rows have `price_per_weight = 0` default-stamped, so
  //      treating 0 as committed would hide the phantom preview the
  //      user wants to see. PriceCell makes the same demotion in lockstep.
  //   2. else a pending AI-modify on `pricePerWeight` from
  //      `ai_suggestions[]` (PriceCell renders this as the new value
  //      with a strike-through of the old).
  //   3. else a legacy `proposalDiff` price override (old AI-proposal
  //      pricing path; PriceCell reads it as a fallback when no
  //      `ai_suggestions[]` entry exists).
  //   4. else the phantom preview from the partner/group/product
  //      hierarchy.
  // Preview eligibility matches PriceCell exactly: gated on
  // `proposedRaw == null` (i.e. no AI proposal at all) rather than on
  // `Number.isFinite`, so NaN proposals suppress the preview the same
  // way PriceCell does.
  const committedPrice =
    flow.price_per_weight != null && flow.price_per_weight !== 0
      ? flow.price_per_weight
      : null;
  const hasUserPrice = committedPrice != null;
  const proposalDiff = isProposalFlow(flow) ? flow.__proposalDiff : null;
  const proposalDiffRaw =
    proposalDiff?.fieldDiffs.price?.newValue ??
    proposalDiff?.fieldDiffs.price_per_weight?.newValue ??
    null;
  const aiChange = !hasUserPrice
    ? getAiChangeValues(flow, "pricePerWeight")
    : null;
  // Same explicit-null tolerance as PriceCell: `aiChange.to` may be
  // null (AI proposes clearing the price), so we only fall through to
  // `proposalDiff` when there is no `aiChange` at all.
  const proposedRaw = aiChange ? aiChange.to : proposalDiffRaw;
  const proposedNum =
    proposedRaw != null && proposedRaw !== "" ? Number(proposedRaw) : null;
  const hasAiPrice = proposedNum != null && Number.isFinite(proposedNum);

  const partnerId = flow.partner_id ?? preOrder.partner_id ?? null;
  // Lockstep with PriceCell: suppress the phantom preview on purchase
  // rows. The lookup hierarchy is sales-side, so showing it as the
  // suggested total for a supplier order would surface the wrong number.
  const isPurchase = preOrder.category === PreOrderCategory.PURCHASES;
  // Gate on `proposedRaw == null` (no AI proposal at all) so NaN
  // proposals suppress the preview the same way PriceCell does.
  const previewEligible = !hasUserPrice && proposedRaw == null && !isPurchase;
  const preview =
    previewEligible && flow.product_id
      ? (lookups?.getEffectivePriceWithSource?.(flow.product_id, partnerId) ??
        null)
      : null;
  const isPreview = preview != null;

  const overridePrice = hasAiPrice ? proposedNum : (preview?.value ?? null);

  // Per-PO Dexie flows are lean (`flow.product` undefined); weight-priced
  // totals need the product for the compute-time weight override, so
  // resolve it from the lookups productMap before delegating.
  const productMap = lookups?.productMap;
  const flowForTotal = useMemo(() => {
    if (flow.product || !flow.product_id) return flow;
    const product = productMap?.get(flow.product_id);
    return product ? { ...flow, product } : flow;
  }, [flow, productMap]);
  const total = useMemo(
    () => calculateFlowTotal(flowForTotal, units, overridePrice),
    [flowForTotal, units, overridePrice],
  );

  // Render `-` only when the row has no usable signal (no quantity OR no
  // price available anywhere). A committed `price_per_weight = 0` with a
  // real quantity is a valid zero total and must render as `0.00` — see
  // PR #1508 cubic P2 review on TotalCell.tsx:30.
  const hasQuantity = (flow.quantity ?? 0) > 0 || (flow.weight ?? 0) > 0;
  const hasAnyPrice = hasUserPrice || hasAiPrice || isPreview;
  if (total == null || isNaN(total) || !hasQuantity || !hasAnyPrice) {
    return <span className="text-gray-400 text-xs px-1">-</span>;
  }

  return (
    <div className="flex items-center justify-end gap-1 px-1">
      <span
        className={cn(
          "text-xs",
          isPreview ? "text-gray-400 italic" : "text-gray-900",
        )}
        data-testid="flow-total"
        data-preview={isPreview || undefined}
      >
        {total.toFixed(2)}
      </span>
      <span className="text-[10px] text-gray-400 flex-shrink-0">
        {currencySymbol}
      </span>
    </div>
  );
}
